// Notification.context.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";

import { registerNotificationHandler } from "./notification.service";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";

export type NotificationType = "success" | "error" | "default" | "warning";

export type NotificationPayload = {
  message: string;
  type?: NotificationType;
};

type NotificationWithId = NotificationPayload & {
  id: string;
  isExiting?: boolean;
};

type NotificationContextType = {
  showNotification: (notification: NotificationPayload) => void;
};

const NotificationContext = createContext<NotificationContextType | null>(null);

const AUTO_DISMISS_DELAY = 6000;
const EXIT_ANIMATION_DURATION = 300; // Duration of exit animation

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<NotificationWithId[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const showNotification = (payload: NotificationPayload) => {
    const id = Date.now().toString() + Math.random().toString(36);
    const notification: NotificationWithId = {
      ...payload,
      type: payload.type || "default",
      id,
      isExiting: false,
    };

    // Add to top of stack
    setNotifications((prev) => [notification, ...prev]);

    // Set auto-dismiss timeout
    const timeout = setTimeout(() => {
      removeNotification(id);
    }, AUTO_DISMISS_DELAY);

    timeoutsRef.current.set(id, timeout);
  };

  const removeNotification = (id: string) => {
    // Mark as exiting first
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isExiting: true } : n)),
    );

    // Clear timeout
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }

    // Actually remove after animation completes
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, EXIT_ANIMATION_DURATION);
  };

  useEffect(() => {
    registerNotificationHandler(showNotification);

    // Cleanup on unmount
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}

      {notifications.length > 0 && (
        <div className="fixed bottom-0 right-0 z-[100] flex flex-col gap-3 p-6 pointer-events-none">
          {notifications.map((notification, index) => (
            <BaseNotifModal
              key={notification.id}
              type={notification.type!}
              isExiting={notification.isExiting || false}
              onClose={() => removeNotification(notification.id)}
            >
              {notification.message}
            </BaseNotifModal>
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotification must be used inside NotificationProvider");
  }
  return ctx;
};

interface BaseNotifModalProps {
  children: React.ReactNode;
  onClose: () => void;
  type: NotificationType;
  isExiting: boolean;
}

export default function BaseNotifModal({
  children,
  onClose,
  type,
  isExiting,
}: BaseNotifModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Trigger enter animation
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const typeStyles = {
    success: " text-neutral-900",
    error: " text-neutral-900",
    warning: " text-neutral-900",
    default: "  text-neutral-900",
  };

  return (
    <div
      className={`
        ${typeStyles[type]} backdrop-blur-sm hover:bg-white bg-wshadow_90
        border-1 rounded-2xl py-3 px-5 shadow-lg shadow-neutral-300 
        pointer-events-auto flex flex-row items-center gap-4 min-w-300 max-w-500
        transition-all duration-300 ease-out
        ${
          isExiting
            ? "opacity-0 translate-x-full"
            : isVisible
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-full"
        }
      `}
      style={{
        transitionProperty: "opacity, transform, margin-top, max-height",
      }}
    >
      {/* Icon indicator */}
      <div className={`flex flex-row items-center`}>
        {type === "success" && (
          <Icon1 name="done2" size={24} color={colors.emerald_400}></Icon1>
        )}
        {type === "error" && (
          <Icon1 name="warning" size={24} color={colors.red_400}></Icon1>
        )}
        {type === "warning" && (
          <Icon1 name="warning" size={24} color={colors.amber_500}></Icon1>
        )}
      </div>

      {/* Message */}
      <div className="flex-1 text-h2">{children}</div>

      {/* Close button */}
      <button
        onClick={onClose}
        className=" opacity-60 hover:opacity-100 transition-opacity flex flex-row items-center justify-center "
      >
        <Icon1 name="effacer2" size={20} color={colors.neutral_500}></Icon1>
      </button>
    </div>
  );
}
